import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { AppRoutes, SessionRoutes } from '@core';
import { DrillDetailQueryHandler, DrillDetailQueryResult, DrillDetailViewModel } from '@drills';
import {
  SzAppShellComponent,
  SzBottomSheetComponent,
  SzButtonComponent,
  SzCtaBarComponent,
  SzIconButtonComponent,
  SzIconComponent,
  SzNumFocalComponent,
  SzNumpadGridComponent,
  SzNumpadKey,
  SzPageTitleComponent,
  SzScoreChipComponent,
  SzSectionHeadComponent,
  SzSkeletonComponent,
  SzTopBarComponent,
  SzTypePillComponent
} from '@ui';
import { SessionStore } from '../../application/state';

type TargetSelectionKind = 'preset' | 'custom' | 'none';
const PRESETS: ReadonlyArray<number> = [15, 25, 50] as const;
const MAX_CUSTOM_DIGITS: number = 3;

@Component({
  selector: 'lib-session-quick-setup',
  templateUrl: './session-quick-setup.component.html',
  styleUrls: ['./session-quick-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    SzAppShellComponent,
    SzBottomSheetComponent,
    SzTopBarComponent,
    SzPageTitleComponent,
    SzTypePillComponent,
    SzScoreChipComponent,
    SzSectionHeadComponent,
    SzSkeletonComponent,
    SzButtonComponent,
    SzCtaBarComponent,
    SzIconButtonComponent,
    SzIconComponent,
    SzNumFocalComponent,
    SzNumpadGridComponent
  ],
  providers: [DrillDetailQueryHandler]
})
export class SessionQuickSetupComponent implements OnInit {
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly store: SessionStore = inject(SessionStore);
  private readonly detailHandler: DrillDetailQueryHandler = inject(DrillDetailQueryHandler);
  private readonly translate: TranslateService = inject(TranslateService);

  readonly presets: ReadonlyArray<number> = PRESETS;
  readonly drill: WritableSignal<DrillDetailViewModel | null> = signal<DrillDetailViewModel | null>(null);

  private readonly selectionKind: WritableSignal<TargetSelectionKind> = signal<TargetSelectionKind>('none');
  private readonly presetValue: WritableSignal<number> = signal<number>(PRESETS[1]);
  private readonly committedCustomValue: WritableSignal<number | null> = signal<number | null>(null);
  readonly customSheetOpen: WritableSignal<boolean> = signal<boolean>(false);
  readonly customDigits: WritableSignal<string> = signal<string>('');

  readonly selectedPreset: Signal<number | null> = computed(() =>
    this.selectionKind() === 'preset' ? this.presetValue() : null
  );
  readonly customSelected: Signal<boolean> = computed(() => this.selectionKind() === 'custom');
  readonly noneSelected: Signal<boolean> = computed(() => this.selectionKind() === 'none');

  readonly customDraftValue: Signal<number> = computed(() => {
    const d: string = this.customDigits();
    return d.length === 0 ? 0 : Number.parseInt(d, 10);
  });

  readonly customDisplay: Signal<string> = computed(() => this.customDraftValue().toString());

  readonly canSaveCustom: Signal<boolean> = computed(() => this.customDraftValue() > 0);

  readonly numpadKeys: Signal<readonly SzNumpadKey[]> = computed((): readonly SzNumpadKey[] => [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
    { label: '00', value: '00', variant: 'fn' },
    { label: '0', value: '0' },
    { label: this.translate.instant('session.numpad.backspace'), value: 'del', variant: 'fn', icon: 'arrow-back' }
  ]);

  readonly canStart: Signal<boolean> = computed(() => {
    if (this.drill() === null) return false;
    if (this.selectionKind() !== 'custom') return true;
    return (this.committedCustomValue() ?? 0) > 0;
  });

  readonly customSubLabel: Signal<string> = computed(() => {
    const v: number | null = this.committedCustomValue();
    if (v === null || v === 0) return this.translate.instant('session.quickSetup.customSub');
    return `${v} ${this.translate.instant('session.quickSetup.presetRunsLabel')}`;
  });

  ngOnInit(): void {
    this.loadDrill();
  }

  selectPreset(value: number): void {
    this.selectionKind.set('preset');
    this.presetValue.set(value);
  }

  selectCustom(): void {
    this.selectionKind.set('custom');
    this.customDigits.set(this.committedCustomValue() === null ? '' : this.committedCustomValue()!.toString());
    this.customSheetOpen.set(true);
  }

  selectNone(): void {
    this.selectionKind.set('none');
  }

  closeCustomSheet(): void {
    this.customSheetOpen.set(false);
    if (this.committedCustomValue() === null) {
      this.selectionKind.set('none');
    }
  }

  saveCustom(): void {
    if (!this.canSaveCustom()) return;
    this.committedCustomValue.set(this.customDraftValue());
    this.customSheetOpen.set(false);
  }

  onCustomKeyPress(value: string): void {
    if (value === 'del') {
      this.customDigits.update((cur: string) => cur.slice(0, -1));
      return;
    }
    if (value === '00') {
      if (this.customDigits().length + 2 > MAX_CUSTOM_DIGITS) return;
      this.customDigits.update((cur: string) => (cur === '' ? '0' : cur + '00'));
      return;
    }
    this.customDigits.update((cur: string) => (cur.length >= MAX_CUSTOM_DIGITS ? cur : cur + value));
  }

  start(): void {
    const d: DrillDetailViewModel | null = this.drill();
    if (d === null) return;
    const target: number | null = this.resolveTargetRuns();
    this.store.initDraft([
      {
        drillId: d.id,
        drillName: d.name,
        drillDescription: d.description,
        planNote: null,
        scoring: d.scoring,
        shotsCount: d.shotsCount,
        targetRuns: target,
        runs: []
      }
    ]);
    void this.router.navigate(['/', AppRoutes.SESSION, SessionRoutes.ACTIVE]);
  }

  private resolveTargetRuns(): number | null {
    switch (this.selectionKind()) {
      case 'preset':
        return this.presetValue();
      case 'custom':
        return this.committedCustomValue();
      case 'none':
        return null;
    }
  }

  private loadDrill(): void {
    const drillId: string = this.route.snapshot.paramMap.get('drillId') ?? '';
    this.detailHandler
      .query(drillId)
      .pipe(take(1))
      .subscribe((r: DrillDetailQueryResult) => this.drill.set(r.drill));
  }
}
